import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { RiDeleteBin6Line } from "react-icons/ri"
import { FiPlus } from "react-icons/fi"

import IconBtn from "../../../common/IconBtn"
import ConfirmationModal from "../../../common/ConfirmationModal"
import {
  getQuizForInstructor,
  saveQuiz,
  deleteQuiz,
} from "../../../../services/operations/quizAPI"

const emptyQuestion = () => ({
  questionText: "",
  options: ["", ""],
  correctOption: 0,
  explanation: "",
})

//Authoring the end of course quiz. Students only see this once they finish the
//course, and they never receive the correct answers until they submit.
const QuizBuilder = ({ courseId }) => {
  const { token } = useSelector((state) => state.auth)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [confirmationModal, setConfirmationModal] = useState(null)

  const [exists, setExists] = useState(false)
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const [passingScore, setPassingScore] = useState(60)
  const [questions, setQuestions] = useState([emptyQuestion()])

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      const quiz = await getQuizForInstructor(courseId, token)

      if (quiz) {
        setExists(true)
        setTitle(quiz.title || "")
        setDescription(quiz.description || "")
        setPassingScore(quiz.passingScore ?? 60)
        setQuestions(
          quiz.questions?.length
            ? quiz.questions.map((q) => ({
                questionText: q.questionText || "",
                options: q.options?.length ? q.options : ["", ""],
                correctOption: q.correctOption ?? 0,
                explanation: q.explanation || "",
              }))
            : [emptyQuestion()]
        )
      }

      setLoading(false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId])

  //Every mutation replaces the array rather than editing in place, so React sees
  //the change
  const updateQuestion = (index, changes) =>
    setQuestions((prev) =>
      prev.map((q, i) => (i === index ? { ...q, ...changes } : q))
    )

  const updateOption = (qIndex, oIndex, value) =>
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIndex
          ? { ...q, options: q.options.map((o, j) => (j === oIndex ? value : o)) }
          : q
      )
    )

  const addOption = (qIndex) =>
    setQuestions((prev) =>
      prev.map((q, i) => (i === qIndex ? { ...q, options: [...q.options, ""] } : q))
    )

  const removeOption = (qIndex, oIndex) =>
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex || q.options.length <= 2) return q

        const options = q.options.filter((_, j) => j !== oIndex)

        //if the removed option was the answer, or sat before it, the stored index
        //would otherwise point at the wrong option
        let correctOption = q.correctOption
        if (oIndex === correctOption) correctOption = 0
        else if (oIndex < correctOption) correctOption -= 1

        return { ...q, options, correctOption }
      })
    )

  const handleSave = async () => {
    setSaving(true)
    const ok = await saveQuiz(
      { courseId, title, description, passingScore: Number(passingScore), questions },
      token
    )
    setSaving(false)
    if (ok) setExists(true)
  }

  const handleDelete = async () => {
    const ok = await deleteQuiz(courseId, token)

    if (ok) {
      setExists(false)
      setTitle("")
      setDescription("")
      setPassingScore(60)
      setQuestions([emptyQuestion()])
    }

    setConfirmationModal(null)
  }

  if (loading) return <div className="spinner"></div>

  return (
    <div className="rounded-md border-[1px] border-richblack-700 bg-richblack-800 p-6">
      <h2 className="text-xl font-semibold text-richblack-5">End of course quiz</h2>
      <p className="mt-1 text-sm text-richblack-300">
        Unlocks for a student once they have completed every lecture. Retakes are
        unlimited and their best score is kept.
      </p>

      <div className="mt-6 flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-richblack-5">
            Quiz title <sup className="text-pink-200">*</sup>
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Final assessment"
            className="rounded-md bg-richblack-700 p-3 text-richblack-5"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm text-richblack-5">Description</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional"
            className="rounded-md bg-richblack-700 p-3 text-richblack-5"
          />
        </label>

        <label className="flex w-48 flex-col gap-1">
          <span className="text-sm text-richblack-5">Pass mark (%)</span>
          <input
            type="number"
            min="1"
            max="100"
            value={passingScore}
            onChange={(e) => setPassingScore(e.target.value)}
            className="rounded-md bg-richblack-700 p-3 text-richblack-5"
          />
        </label>
      </div>

      <div className="mt-8 flex flex-col gap-6">
        {questions.map((question, qIndex) => (
          <div
            key={qIndex}
            className="rounded-md border-[1px] border-richblack-700 bg-richblack-900 p-5"
          >
            <div className="flex items-start justify-between gap-4">
              <p className="font-semibold text-richblack-5">Question {qIndex + 1}</p>

              {questions.length > 1 && (
                <button
                  title="Remove question"
                  onClick={() =>
                    setQuestions((prev) => prev.filter((_, i) => i !== qIndex))
                  }
                  className="text-lg text-pink-200"
                >
                  <RiDeleteBin6Line />
                </button>
              )}
            </div>

            <input
              type="text"
              value={question.questionText}
              onChange={(e) => updateQuestion(qIndex, { questionText: e.target.value })}
              placeholder="Question text"
              className="mt-3 w-full rounded-md bg-richblack-700 p-3 text-richblack-5"
            />

            <p className="mt-4 text-sm text-richblack-300">
              Select the radio button next to the correct answer
            </p>

            <div className="mt-2 flex flex-col gap-2">
              {question.options.map((option, oIndex) => (
                <div key={oIndex} className="flex items-center gap-3">
                  <input
                    type="radio"
                    name={`correct-${qIndex}`}
                    checked={question.correctOption === oIndex}
                    onChange={() => updateQuestion(qIndex, { correctOption: oIndex })}
                    className="h-4 w-4 accent-yellow-50"
                  />
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                    placeholder={`Option ${oIndex + 1}`}
                    className="flex-1 rounded-md bg-richblack-700 p-2 text-richblack-5"
                  />
                  {question.options.length > 2 && (
                    <button
                      title="Remove option"
                      onClick={() => removeOption(qIndex, oIndex)}
                      className="text-pink-200"
                    >
                      <RiDeleteBin6Line />
                    </button>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={() => addOption(qIndex)}
              className="mt-3 flex items-center gap-1 text-sm text-yellow-50"
            >
              <FiPlus /> Add option
            </button>

            <input
              type="text"
              value={question.explanation}
              onChange={(e) => updateQuestion(qIndex, { explanation: e.target.value })}
              placeholder="Explanation, shown after they submit (optional)"
              className="mt-4 w-full rounded-md bg-richblack-700 p-3 text-richblack-5"
            />
          </div>
        ))}
      </div>

      <button
        onClick={() => setQuestions((prev) => [...prev, emptyQuestion()])}
        className="mt-4 flex items-center gap-1 font-medium text-yellow-50"
      >
        <FiPlus /> Add question
      </button>

      <div className="mt-8 flex items-center gap-4">
        <IconBtn
          onClick={handleSave}
          disabled={saving}
          text={saving ? "Saving..." : exists ? "Save changes" : "Create quiz"}
        />

        {exists && (
          <button
            onClick={() =>
              setConfirmationModal({
                text1: "Delete this quiz?",
                text2: "Every student attempt for this course is deleted too.",
                btn1Text: "Delete",
                btn2Text: "Cancel",
                btn1Handler: handleDelete,
                btn2Handler: () => setConfirmationModal(null),
              })
            }
            className="rounded-md bg-richblack-700 px-5 py-2 font-semibold text-pink-200"
          >
            Delete quiz
          </button>
        )}
      </div>

      {confirmationModal && <ConfirmationModal modalData={confirmationModal} />}
    </div>
  )
}

export default QuizBuilder
